import { formatDate, formatPhone, type Property } from "@elektr/shared";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Badge, Card, EmptyState, PageHeader, Spinner } from "@/components/ui/card";
import { Input, Select } from "@/components/ui/form";
import { Pagination } from "@/components/ui/pagination";
import { api, unwrap } from "@/lib/api";
import { useDebounced } from "@/lib/hooks";
import { useT } from "@/lib/i18n";
import { STATUS_KEYS, STATUS_TONE, TYPE_KEYS } from "@/lib/labels";

const PAGE_SIZE = 20;

export function PropertiesPage() {
  const { t } = useT();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const page = Number(sp.get("page") ?? 1) || 1;
  const status = sp.get("status") ?? "";
  const ordering = sp.get("ordering") ?? "-created_at";
  const urlQ = sp.get("q") ?? "";

  const [text, setText] = useState(urlQ);
  const debounced = useDebounced(text);
  useEffect(() => {
    if (debounced !== urlQ) update({ q: debounced, page: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  function update(patch: Record<string, string>) {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setSp(next, { replace: true });
  }

  const query = useQuery({
    queryKey: ["properties", { page, status, ordering, q: urlQ }],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/properties/", {
          params: {
            query: {
              page,
              page_size: PAGE_SIZE,
              status: status || undefined,
              ordering,
              q: urlQ || undefined,
            },
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });

  const rows = query.data?.results ?? [];

  return (
    <>
      <PageHeader
        title={t("nav.properties")}
        actions={
          <Button asChild>
            <Link to="/properties/new">
              <Plus className="size-4" /> {t("props.new")}
            </Link>
          </Button>
        }
      />
      <Card className="mb-4 grid gap-3 sm:grid-cols-[1fr_12rem_12rem]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-3.5 size-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder={t("props.search")}
            aria-label={t("props.search")}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
        <Select
          aria-label={t("props.status")}
          value={status}
          onChange={(e) => update({ status: e.target.value, page: "" })}
        >
          <option value="">{t("props.all_statuses")}</option>
          {(["draft", "ready", "archived"] as const).map((s) => (
            <option key={s} value={s}>
              {t(STATUS_KEYS[s])}
            </option>
          ))}
        </Select>
        <Select
          aria-label={t("props.sort")}
          value={ordering}
          onChange={(e) => update({ ordering: e.target.value, page: "" })}
        >
          <option value="-created_at">{t("props.sort_new")}</option>
          <option value="created_at">{t("props.sort_old")}</option>
          <option value="address">{t("props.sort_address")}</option>
          <option value="-installed_at">{t("props.sort_installed")}</option>
        </Select>
      </Card>

      {query.isLoading && (
        <div className="p-10 text-center">
          <Spinner className="size-8" />
        </div>
      )}
      {query.isError && (
        <p role="alert" className="text-danger">
          {t("common.error")}
        </p>
      )}
      {query.data && rows.length === 0 && <EmptyState>{t("props.empty")}</EmptyState>}

      {rows.length > 0 && (
        <>
          {/* planshet/kompyuter: jadval */}
          <Card className="hidden overflow-x-auto p-0 md:block">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-muted text-muted-foreground">
                <tr>
                  {[
                    "props.col_address",
                    "props.col_district",
                    "props.col_owner",
                    "props.col_electricians",
                    "props.col_installed",
                    "props.status",
                  ].map((k) => (
                    <th key={k} className="px-4 py-3 font-semibold">
                      {t(k)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr
                    key={p.id}
                    className="cursor-pointer border-b border-border last:border-0 hover:bg-muted"
                    onClick={() => nav(`/properties/${p.id}`)}
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/properties/${p.id}`}
                        className="font-semibold hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {p.address} {p.house_number}
                      </Link>
                      <div className="text-xs text-muted-foreground">
                        {t(TYPE_KEYS[p.type ?? "apartment"])}
                      </div>
                    </td>
                    <td className="px-4 py-3">{p.district}</td>
                    <td className="px-4 py-3">
                      <Owner p={p} />
                    </td>
                    <td className="px-4 py-3">
                      {p.electricians_detail
                        .map((e) => e.full_name || formatPhone(e.phone))
                        .join(", ")}
                    </td>
                    <td className="px-4 py-3">{formatDate(p.installed_at)}</td>
                    <td className="px-4 py-3">
                      <Badge tone={STATUS_TONE[p.status ?? "draft"]}>
                        {t(STATUS_KEYS[p.status ?? "draft"])}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* telefon: kartochkalar */}
          <div className="flex flex-col gap-2 md:hidden">
            {rows.map((p) => (
              <Link key={p.id} to={`/properties/${p.id}`}>
                <Card className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate font-semibold">
                      {p.address} {p.house_number}
                    </div>
                    <div className="text-sm text-muted-foreground">{p.district}</div>
                    <div className="text-sm">
                      <Owner p={p} />
                    </div>
                  </div>
                  <Badge tone={STATUS_TONE[p.status ?? "draft"]}>
                    {t(STATUS_KEYS[p.status ?? "draft"])}
                  </Badge>
                </Card>
              </Link>
            ))}
          </div>
          <Pagination
            page={page}
            count={query.data?.count ?? 0}
            pageSize={PAGE_SIZE}
            onPage={(n) => update({ page: String(n) })}
          />
        </>
      )}
    </>
  );
}

function Owner({ p }: { p: Property }) {
  const { t } = useT();
  if (p.owner_detail) return <>{p.owner_detail.full_name || formatPhone(p.owner_detail.phone)}</>;
  if (p.owner_phone)
    return <span className="text-muted-foreground">{formatPhone(p.owner_phone)}</span>;
  return <span className="text-muted-foreground">{t("props.no_owner")}</span>;
}
